<script>
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import { API_BASE_URL } from '../config.js';

  // Labels of the category tree levels (area > macrocategory > course > year)
  const LEVEL_LABELS = ['Area', 'Macrocategory', 'Course', 'Year'];
  const MAX_GROUP_NAME = 128;
  const DUPLICATE_CHECK_TIMEOUT_MS = 3000;

  let userId = writable(''); // Telegram user ID
  let username = writable('');
  let firstName = writable('');
  let initData = writable(''); // Signed Telegram.WebApp.initData (verified by the backend when enabled)
  let groupName = writable('');
  let selectedCategories = writable([]);
  let categories = writable([]);
  let comments = writable('');
  let errorMessage = writable('');
  let successMessage = writable('');
  let submitting = writable(false);
  let loadingCategories = writable(false);
  let categoryLoadFailed = writable(false);
  let showModal = writable(true); // For the guidelines popup
  let duplicateWarning = writable(null);

  onMount(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      const tgUser = window.Telegram.WebApp.initDataUnsafe?.user;
      if (tgUser) {
        userId.set(tgUser.id);
        username.set(tgUser.username || '');
        firstName.set([tgUser.first_name, tgUser.last_name].filter(Boolean).join(' '));
      }
      initData.set(window.Telegram.WebApp.initData || '');
    }
    loadCategories();
  });

  // Fetch categories from the backend
  async function fetchCategories(parentID = 0) {
    try {
      const response = await fetch(`${API_BASE_URL}/categories?parent_id=${parentID}`);
      if (!response.ok) {
        errorMessage.set('Failed to load categories.');
        return null;
      }
      return await response.json();
    } catch (e) {
      errorMessage.set('Failed to load categories.');
      return null;
    }
  }

  async function loadCategories(parentID = 0, level = 0) {
    loadingCategories.set(true);
    categoryLoadFailed.set(false);
    try {
      const fetchedCategories = await fetchCategories(parentID);
      if (fetchedCategories === null) {
        categoryLoadFailed.set(true);
        return;
      }
      categories.update(current => {
        const next = current.slice(0, level);
        if (fetchedCategories.length > 0) {
          next[level] = fetchedCategories;
        }
        return next;
      });
    } finally {
      loadingCategories.set(false);
    }
  }

  function handleCategoryChange(event, level) {
    const selectedCategoryID = Number(event.target.value);

    selectedCategories.update(current => {
      const next = current.slice(0, level);
      if (selectedCategoryID) {
        next[level] = selectedCategoryID;
      }
      return next;
    });
    categories.update(current => current.slice(0, level + 1));

    if (selectedCategoryID) {
      loadCategories(selectedCategoryID, level + 1);
    }
  }

  // Validate the form
  function validateForm() {
    const leaf = $selectedCategories[$selectedCategories.length - 1];
    if (!leaf) {
      errorMessage.set('Please select the category of the group.');
      return false;
    }
    if ($loadingCategories) {
      errorMessage.set('Please wait while the category selection is loading.');
      return false;
    }
    if ($categoryLoadFailed) {
      errorMessage.set('Could not verify the selected category. Please select it again.');
      return false;
    }
    if ($categories.length > $selectedCategories.length) {
      errorMessage.set('Please complete the category selection (course and year).');
      return false;
    }
    if (!String($groupName).trim()) {
      errorMessage.set('Group name is required.');
      return false;
    }
    if (String($groupName).trim().length > MAX_GROUP_NAME) {
      errorMessage.set(`Group name is too long (max ${MAX_GROUP_NAME} characters).`);
      return false;
    }
    errorMessage.set('');
    return true;
  }

  function notify(text) {
    const webApp = window.Telegram && window.Telegram.WebApp;
    if (webApp && typeof webApp.showAlert === 'function') {
      try {
        webApp.showAlert(text);
        return;
      } catch (e) {
        // older Telegram clients do not support showAlert
      }
    }
    alert(text);
  }

  function requestPayload() {
    return {
      user_id: Number($userId) || 0, // Telegram user ID
      username: $username,
      first_name: $firstName,
      init_data: $initData,
      group_name: String($groupName).trim(),
      category: $selectedCategories[$selectedCategories.length - 1],
      comments: $comments,
    };
  }

  function selectedCategoryName(level) {
    const selectedID = $selectedCategories[level];
    const choices = $categories[level] || [];
    const selected = choices.find(category => Number(category.id) === Number(selectedID));
    return selected ? selected.name : '';
  }

  function selectedCategoryPath() {
    return $selectedCategories
      .map((_, level) => selectedCategoryName(level))
      .filter(Boolean)
      .join(' · ');
  }

  // This check is advisory. A lookup failure must never prevent a request
  // from being submitted and an existing group can be explicitly overridden.
  async function findDuplicate(request) {
    const name = encodeURIComponent(request.group_name);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = controller
      ? setTimeout(() => controller.abort(), DUPLICATE_CHECK_TIMEOUT_MS)
      : null;
    try {
      const response = await fetch(
        `${API_BASE_URL}/groups/duplicate?name=${name}&category=${request.category}`,
        controller ? { signal: controller.signal } : undefined,
      );
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (e) {
      return null;
    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  }

  async function submitRequest() {
    if (!validateForm()) {
      return;
    }
    submitting.set(true);
    successMessage.set('');

    const request = requestPayload();
    const duplicate = await findDuplicate(request);
    if (duplicate && duplicate.normalized_name) {
      request.group_name = duplicate.normalized_name;
      groupName.set(duplicate.normalized_name);
    }
    if (duplicate && duplicate.exists) {
      const matches = Array.isArray(duplicate.matches) && duplicate.matches.length > 0
        ? duplicate.matches
        : [{ group_name: duplicate.group_name || request.group_name, exact: true }];
      duplicateWarning.set({
        request,
        matches,
        categoryPath: selectedCategoryPath(),
      });
      submitting.set(false);
      return;
    }

    await createRequest(request);
  }

  async function createRequest(request) {
    try {
      const response = await fetch(`${API_BASE_URL}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        successMessage.set('Request submitted! Our staff will review it and you will be notified on Telegram.');
        notify('Request submitted successfully! You will be notified on Telegram once it is reviewed.');
        groupName.set('');
        selectedCategories.set([]);
        comments.set('');
        loadCategories();
      } else {
        let message = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          message = errorData.message || message;
        } catch (e) {
          // non-JSON error body
        }
        notify(`Failed to submit request: ${message}`);
      }
    } catch (e) {
      notify('Failed to submit request: network error.');
    } finally {
      submitting.set(false);
    }
  }

  async function confirmDuplicateRequest() {
    const warning = $duplicateWarning;
    if (!warning || $submitting) {
      return;
    }
    duplicateWarning.set(null);
    submitting.set(true);
    successMessage.set('');
    await createRequest(warning.request);
  }

  function cancelDuplicateRequest() {
    duplicateWarning.set(null);
  }

  function searchWithBot() {
    const url = 'https://t.me/politogroupsbot';
    const webApp = window.Telegram && window.Telegram.WebApp;
    if (webApp && typeof webApp.openTelegramLink === 'function') {
      webApp.openTelegramLink(url);
      if (typeof webApp.close === 'function') {
        webApp.close();
      }
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // Close modal to allow form filling
  function closeModal() {
    showModal.set(false);
  }
</script>

<!-- Modal for Guidelines -->
{#if $showModal}
  <div class="modal is-active">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="box">
        <h2 class="title">How it works</h2>
        <ul>
          <li>1) <b>CHECK GROUP DOESN'T EXIST FIRST</b> or your request will be REJECTED, use @politogroupsbot to search for groups instead of hitting "create group" right away.</li>
          <li>2) <b>GROUP NAME MUST BE THE SAME AS THE COURSE NAME IN THE UNIVERSITY PORTAL.</b></li>
          <li>3) Pick the course and year the new group is for and give it a name.</li>
          <li>4) Our staff reviews the request. You will receive a Telegram message with the outcome.</li>
          <li>5) If approved, the group is created by our GroupFactory with the standard moderation setup (moderation staff and helper bot already inside) and you receive the invite link.</li>
          <li>6) The group is added to the PolitoGroupsBot directory so other students can find it.</li>
        </ul><br><br>
        <h2 class="title">Mandatory requirements</h2>
        <ul>
          <li>1) Search the PolitoGroupsBot directory first: do not request a group that already exists for the same course and year.</li>
          <li>2) One request per course/year. Duplicate or joke requests will be rejected.</li>
          <li>3) Groups are for study purposes only and must not be affiliated with political, religious, or unlawful organizations, nor be used to promote self-interests.</li>
          <li>4) Don't spam or let other people spam inside the group and don't let WhatsApp links circulate in it.</li>
          <li>5) To use this service, you must not be banned from our Telegram network.</li>
          <li>6) Moderation accounts and helper bots added by the GroupFactory must not be removed or demoted.</li>
          <li>7) Do not complain about our policies; they are enforced to maintain service quality.</li>
        </ul><br><br>
        Failing to comply with these requirements will result in rejection or removal of the group from our list with no appeal. Unreasonable complaints will lead to a permanent ban and reporting to Telegram for further investigation.
        <br><br><br>
        <button class="button is-primary" on:click={closeModal}>I understand</button>
      </div>
    </div>
  </div>
{/if}

<!-- Advisory duplicate warning: users may search again or submit anyway. -->
{#if $duplicateWarning}
  <div class="modal is-active" role="dialog" aria-modal="true" aria-labelledby="duplicate-warning-title">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="box">
        <h2 id="duplicate-warning-title" class="title is-4">⚠️ Similar groups may already exist</h2>
        <p>
          We found these possible matches for <strong>{$duplicateWarning.categoryPath}</strong>:
        </p>
        <ul class="mt-3">
          {#each $duplicateWarning.matches as match}
            <li>• <strong>{match.group_name}</strong>{#if match.exact} — equivalent name{/if}</li>
          {/each}
        </ul>
        <p class="mt-4">
          Please search again with <strong>@politogroupsbot</strong> before creating a duplicate.
        </p>
        <p class="mt-4"><strong>Are you sure you still want to submit this request?</strong></p>
        <div class="buttons mt-5">
          <button class="button is-link" type="button" on:click={searchWithBot}>Search with @politogroupsbot</button>
          <button class="button" type="button" on:click={cancelDuplicateRequest}>Go back</button>
          <button class="button is-warning" type="button" on:click={confirmDuplicateRequest} disabled={$submitting}>
            Submit anyway
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Form Content -->
<div class="container" class:disabled={$showModal || $duplicateWarning}>
  {#if $errorMessage}
    <p class="has-text-danger">{$errorMessage}</p>
  {/if}
  {#if $successMessage}
    <p class="has-text-success">{$successMessage}</p>
  {/if}

  {#each $categories as levelCategories, level}
    <div class="field">
      <label class="label" for={`category-level-${level}`}>Select {LEVEL_LABELS[level] || `Category Level ${level + 1}`}</label>
      <div class="control">
        <div class="select">
          <select id={`category-level-${level}`} on:change={(e) => handleCategoryChange(e, level)} value={$selectedCategories[level] ?? ''} disabled={$showModal || $duplicateWarning || $submitting || $loadingCategories}>
            <option value="" disabled>Select {(LEVEL_LABELS[level] || 'a category').toLowerCase()}</option>
            {#each levelCategories as category}
              <option value={category.id}>{category.name}</option>
            {/each}
          </select>
        </div>
      </div>
    </div>
  {/each}

  <div class="field">
    <label class="label" for="group-name">Group Name</label>
    <div class="control">
      <input id="group-name" class="input" type="text" bind:value={$groupName} placeholder="Group Name" maxlength={MAX_GROUP_NAME} disabled={$showModal || $duplicateWarning || $submitting}>
    </div>
  </div>

  <div class="field">
    <label class="label" for="comments">Comments (optional)</label>
    <div class="control">
      <textarea id="comments" class="textarea" bind:value={$comments} placeholder="Why is this group needed? Anything the staff should know..." maxlength="1000" disabled={$showModal || $duplicateWarning || $submitting}></textarea>
    </div>
  </div>

  <div class="control">
    <button class="button is-primary" class:is-loading={$submitting || $loadingCategories} on:click={submitRequest} disabled={$showModal || $duplicateWarning || $submitting || $loadingCategories || $categoryLoadFailed}>Submit request</button>
  </div>
</div>
